import * as React from 'react';
const pct = value => (value == null ? '—' : `${Number(value.toFixed(1))}%`);
export function ReachChart({
  series,
  comparison = [],
  onSelect,
  unavailableReason
}: {
  series: any[];
  comparison?: any[];
  onSelect: () => void;
  unavailableReason?: string;
}) {
  const container = React.useRef<HTMLDivElement>(null),
    [width, setWidth] = React.useState(550);
  React.useEffect(() => {
    const resize = () => {
      if (container.current) setWidth(Math.max(230, container.current.clientWidth));
    };
    resize();
    const observer = new ResizeObserver(resize);
    if (container.current) observer.observe(container.current);
    return () => observer.disconnect();
  }, []);
  const valid = series.filter(point => point.rate != null);
  if (!valid.length) {
    return (
      <div className="sli-chartEmpty">
        <strong>
          {unavailableReason ? 'Historical participation trend unavailable' : 'Waiting for participation records'}
        </strong>
        <span>
          {unavailableReason || 'A trend will appear when dated club or sport participant records are available.'}
        </span>
      </div>
    );
  }
  const line = (values: any[]) => {
    let pen = false;
    return values
      .map((point, index) => {
        if (point.rate == null) {
          pen = false;
          return '';
        }
        const command = pen ? 'L' : 'M';
        pen = true;
        return `${command}${44 + (index * (width - 70)) / Math.max(1, values.length - 1)},${185 - point.rate * 1.55}`;
      })
      .join(' ');
  };
  return (
    <div ref={container}>
      <button
        type="button"
        className="sli-chartButton"
        onClick={onSelect}
        aria-label="View students behind the reach chart"
      >
        <svg
          className="sli-line"
          viewBox={`0 0 ${width} 225`}
          role="img"
          aria-label={`Cumulative reach ${pct(valid[valid.length - 1].rate)}`}
        >
          {[0, 25, 50, 75, 100].map(value => (
            <g key={value}>
              <line x1="40" x2={width - 22} y1={185 - value * 1.55} y2={185 - value * 1.55} className="sli-gridLine" />
              <text x="30" y={189 - value * 1.55} textAnchor="end">
                {value}%
              </text>
            </g>
          ))}
          {comparison.length > 0 && <path d={line(comparison)} className="sli-previousLine" />}
          <path d={line(series)} className="sli-currentLine" />
          <text x="44" y="213">
            Period start
          </text>
          <text x={width - 26} y="213" textAnchor="end">
            {new Date(valid[valid.length - 1].at).toLocaleDateString('en-GB')}
          </text>
          {valid.map((point, index) => (
            <circle
              key={point.at}
              cx={44 + (series.indexOf(point) * (width - 70)) / Math.max(1, series.length - 1)}
              cy={185 - point.rate * 1.55}
              r="3"
              className="sli-currentDot"
            >
              <title>
                {new Date(point.at).toLocaleDateString('en-GB')}: {pct(point.rate)}
              </title>
            </circle>
          ))}
        </svg>
      </button>
      <details className="sli-data">
        <summary>View data</summary>
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Date</th>
              <th>Confirmed</th>
              <th>Roster</th>
              <th>Reach</th>
            </tr>
          </thead>
          <tbody>
            {valid.map(point => (
              <tr key={point.at}>
                <td>{new Date(point.at).toLocaleDateString('en-GB')}</td>
                <td>{point.participating}</td>
                <td>{point.total}</td>
                <td>{pct(point.rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}
export function EquityChart({ items, onSelect }: { items: any[]; onSelect: (filter: string) => void }) {
  return (
    <>
      <div className="sli-equity">
        {items.map(item => (
          <button
            key={item.field}
            type="button"
            disabled={item.gap == null}
            className="sli-equityRow"
            onClick={() => onSelect(item.filter)}
          >
            <span>
              {item.label}
              <small>{item.group.total == null ? 'Unknown or protected' : `${item.group.total} known students`}</small>
            </span>
            <span className="sli-dumbbell">
              {[0, 25, 50, 75, 100].map(value => (
                <i className="sli-tick" key={value} style={{ left: `${value}%` }} />
              ))}
              {item.gap != null && (
                <>
                  <i
                    className="sli-connector"
                    style={{ left: `${Math.min(item.group.rate, item.peers.rate)}%`, width: `${Math.abs(item.gap)}%` }}
                  />
                  <b className="sli-groupDot" style={{ left: `${item.group.rate}%` }} />
                  <b className="sli-peerDot" style={{ left: `${item.peers.rate}%` }} />
                </>
              )}
            </span>
            <strong className={item.gap != null && item.gap < 0 ? 'sli-negative' : ''}>
              {item.gap == null ? '—' : `${item.gap > 0 ? '+' : ''}${Number(item.gap.toFixed(1))} pts`}
            </strong>
          </button>
        ))}
      </div>
      <p className="sli-help">Blue: group · Grey: known peers. Unknown values are excluded from both sides.</p>
      <details className="sli-data">
        <summary>View data</summary>
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Group</th>
              <th>Reach</th>
              <th>Peers</th>
              <th>Unknown</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.field}>
                <td>{item.label}</td>
                <td>{pct(item.group.rate)}</td>
                <td>{pct(item.peers.rate)}</td>
                <td>{item.unknown == null ? 'Protected' : item.unknown}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </>
  );
}
export function Heatmap({ rows, onSelect }: { rows: any[]; onSelect: (year: string, filter: string) => void }) {
  return (
    <div className="sli-tableWrap">
      <table className="sli-table sli-heatmap">
        <caption>Recorded reach by year and characteristic</caption>
        <thead>
          <tr>
            <th>Year</th>
            {((rows[0] && rows[0].cells) || []).map(cell => (
              <th key={cell.field}>{cell.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.key}>
              <th>{row.label}</th>
              {row.cells.map(cell => (
                <td key={cell.field}>
                  <button
                    type="button"
                    disabled={cell.rate == null}
                    onClick={() => onSelect(row.key, cell.filter)}
                    style={
                      cell.rate == null
                        ? {}
                        : {
                            background: `color-mix(in srgb, var(--action-primary,#227ebd) ${Math.round(
                              15 + cell.rate * 0.65
                            )}%, var(--surface-card,#fff))`,
                            color: cell.rate > 58 ? '#fff' : 'var(--text-strong,#0f172a)'
                          }
                    }
                  >
                    {cell.suppressed ? 'Protected' : pct(cell.rate)}
                  </button>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
export function GenderChart({ rows, onSelect }: { rows: any[]; onSelect: (year: string, filter: string) => void }) {
  return (
    <>
      <div className="sli-genderLegend">
        <span>Girls</span>
        <span>Boys</span>
      </div>
      {rows.map(row => (
        <div className="sli-genderRow" key={row.key}>
          <span>{row.label}</span>
          <button
            type="button"
            disabled={row.group.rate == null}
            onClick={() => onSelect(row.key, 'gender:FEMALE')}
            aria-label={`${row.label} girls ${pct(row.group.rate)}`}
          >
            <i style={{ width: `${row.group.rate || 0}%` }} />
            <b>{pct(row.group.rate)}</b>
          </button>
          <button
            type="button"
            disabled={row.peers.rate == null}
            onClick={() => onSelect(row.key, 'gender:MALE')}
            aria-label={`${row.label} boys ${pct(row.peers.rate)}`}
          >
            <i style={{ width: `${row.peers.rate || 0}%` }} />
            <b>{pct(row.peers.rate)}</b>
          </button>
        </div>
      ))}
      <p className="sli-help">
        Historical gender is unavailable where it was not recorded. No current value is substituted.
      </p>
      <details className="sli-data">
        <summary>View data</summary>
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Year</th>
              <th>Girls</th>
              <th>Boys</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.key}>
                <td>{row.label}</td>
                <td>{pct(row.group.rate)}</td>
                <td>{pct(row.peers.rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </>
  );
}
export function Sparkline({ series }: { series: any[] }) {
  const valid = series.filter(point => point.present != null);
  if (!valid.length) return <span className="sli-muted">No series</span>;
  const max = Math.max(1, ...valid.map(point => point.present)),
    min = Math.min(...valid.map(point => point.present));
  return (
    <svg className="sli-spark" viewBox="0 0 96 28" role="img" aria-label="Confirmed attendance in recent sessions">
      <path
        className="sli-currentLine"
        d={valid
          .map(
            (point, index) =>
              `${index ? 'L' : 'M'}${3 + (index * 90) / Math.max(1, valid.length - 1)},${24 -
                ((point.present - min) / Math.max(1, max - min)) * 20}`
          )
          .join(' ')}
      >
        <title>{valid.map(point => point.present).join(', ')}</title>
      </path>
    </svg>
  );
}
